import { useEffect, useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { chatbotService, type ChatSettings, type ChatStyleConfig } from '@/services/chatbotService';
import { demoService } from '@/services/demoService';
import { projectService, type ProjectDto } from '@/services/projectService';
import { apiJson } from '@/lib/http';

type EmbedChoice = 'bubble' | 'iframe';

const Demo = () => {
  const [chatbotId, setChatbotId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [settings, setSettings] = useState<ChatSettings | null>(null);
  const [loadingSettings, setLoadingSettings] = useState<boolean>(true);
  const [chatbots, setChatbots] = useState<ChatSettings[]>([]);
  const [embedChoice, setEmbedChoice] = useState<EmbedChoice>('bubble');
  const [saving, setSaving] = useState<boolean>(false);
  const [companyName, setCompanyName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [generating, setGenerating] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [fakeData, setFakeData] = useState<string>('');
  const [loadingFakeData, setLoadingFakeData] = useState<boolean>(false);
  const [savingFakeData, setSavingFakeData] = useState<boolean>(false);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('TEAM');

  useEffect(() => {
    (async () => {
      try {
        setLoadingSettings(true);
        setSettings(null);
        setChatbotId(null);
        const list = await chatbotService.listSettings(selectedProjectId === 'TEAM' ? undefined : selectedProjectId);
        
        setChatbots(list);
        if (list.length > 0) {
          const firstId = list[0].id;
          setChatbotId(firstId);
          const s = await chatbotService.getSettingsById(firstId);
          if (s) {
            setSettings(s);
          } else {
            setChatbotId(null);
            setSettings(null);
          }
        }
      } catch {
        // ignore
      } finally {
        setLoadingSettings(false);
      }
    })();
  }, [selectedProjectId]);

  useEffect(() => {
    (async () => {
      try {
        const list = await projectService.list();
        setProjects(list);
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!chatbotId) return;
      try {
        setLoadingFakeData(true);
        const fd = await demoService.getFakeData(chatbotId);
        let pretty = fd || '';
        try {
          // Pretty-print if valid JSON
          const parsed = JSON.parse(pretty);
          pretty = JSON.stringify(parsed, null, 2);
        } catch {
          // leave as-is if not JSON
        }
        setFakeData(pretty);
      } catch {
        // ignore
      } finally {
        setLoadingFakeData(false);
      }
    })();
  }, [chatbotId]);

  const handleCreate = async () => {
    try {
      setCreating(true);
      const s = await chatbotService.createSettings(selectedProjectId === 'TEAM' ? undefined : selectedProjectId);
      setSettings(s);
      setChatbotId(s.id);
      setChatbots((prev) => [s, ...prev]);
      toast('Chatbot created', { description: `ID: ${s.id}` });
    } catch (e) {
      toast('Failed to create chatbot', { description: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!chatbotId) return;
    const confirmDelete = window.confirm('Delete this chatbot? This cannot be undone.');
    if (!confirmDelete) return;
    try {
      await chatbotService.deleteSettings(chatbotId);
      toast('Chatbot deleted');
      setChatbots((prev) => prev.filter((c) => c.id !== chatbotId));
      // Select next available chatbot if any
      setSettings(null);
      const next = chatbots.filter((c) => c.id !== chatbotId);
      if (next.length > 0) {
        const nextId = next[0].id;
        setChatbotId(nextId);
        setLoadingSettings(true);
        const s = await chatbotService.getSettingsById(nextId);
        setSettings(s);
        setLoadingSettings(false);
      } else {
        setChatbotId(null);
      }
    } catch (e) {
      toast('Failed to delete chatbot', { description: e instanceof Error ? e.message : 'Unknown error' });
    }
  };

  const handleSelectChatbot = async (id: string) => {
    try {
      setLoadingSettings(true);
      setChatbotId(id);
      const s = await chatbotService.getSettingsById(id);
      setSettings(s);
    } catch {
      // ignore
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSaveFakeData = async () => {
    try {
      setSavingFakeData(true);
      await demoService.updateFakeData(chatbotId, fakeData);
      toast('Fake data saved');
    } catch (e) {
      toast('Failed to save fake data', { description: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setSavingFakeData(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      const updated = await chatbotService.updateSettings(settings);
      setSettings(updated);
      toast('Chatbot updated');
    } catch (e) {
      toast('Failed to update chatbot', { description: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setSaving(false);
    }
  };

  const updateStyle = <K extends keyof ChatStyleConfig['styles']>(key: K, value: ChatStyleConfig['styles'][K]) => {
    if (!settings) return;
    setSettings({ ...settings, styles: { ...settings.styles, [key]: value } });
  };

  const addInitialMessage = () => {
    if (!settings) return;
    setSettings({ ...settings, initialMessages: [...settings.initialMessages, { message: '', instructions: '' }] });
  };

  const updateInitialMessage = (index: number, key: 'message' | 'instructions', value: string) => {
    if (!settings) return;
    const next = settings.initialMessages.map((m, i) => (i === index ? { ...m, [key]: value } : m));
    setSettings({ ...settings, initialMessages: next });
  };

  const removeInitialMessage = (index: number) => {
    if (!settings) return;
    const next = settings.initialMessages.filter((_, i) => i !== index);
    setSettings({ ...settings, initialMessages: next });
  };

  const addBubbleMessage = () => {
    if (!settings) return;
    setSettings({ ...settings, bubbleMessages: [...settings.bubbleMessages, { message: '', instructions: '' }] });
  };

  const updateBubbleMessage = (index: number, key: 'message' | 'instructions', value: string) => {
    if (!settings) return;
    const next = settings.bubbleMessages.map((m, i) => (i === index ? { ...m, [key]: value } : m));
    setSettings({ ...settings, bubbleMessages: next });
  };

  const removeBubbleMessage = (index: number) => {
    if (!settings) return;
    const next = settings.bubbleMessages.filter((_, i) => i !== index);
    setSettings({ ...settings, bubbleMessages: next });
  };

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  };

  const handleConnectGoogleCalendar = async () => {
    try {
      const response = await apiJson('/googlecalendar/connect');
      window.location.href = response as string;
    } catch (e) {
      toast('Failed to connect Google Calendar', { description: e instanceof Error ? e.message : 'Unknown error' });
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      if (!chatbotId) return;
      await demoService.generateDemoData({ settingsId: chatbotId, companyName, demodataDescription: description });
      // Refresh chatbot settings
      try {
        const refreshed = await chatbotService.getSettingsById(chatbotId);
        if (refreshed) setSettings(refreshed);
      } catch {
        // ignore
      }
      // Refresh fake data view
      try {
        const fd = await demoService.getFakeData(chatbotId);
        let pretty = fd || '';
        try {
          const parsed = JSON.parse(pretty);
          pretty = JSON.stringify(parsed, null, 2);
        } catch {
          // leave as-is if not JSON
        }
        setFakeData(pretty);
      } catch {
        // ignore
      }
      toast('Demo data generated', { description: 'Refreshed chatbot settings and data.' });
    } catch (e) {
      toast('Failed to generate demo data', { description: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleUploadBackground = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/png') {
      toast('Only PNG files are allowed');
      return;
    }
    const maxBytes = 3 * 1024 * 1024; // 3 MB
    if (file.size > maxBytes) {
      toast('Image must be smaller than 3 MB');
      return;
    }
    try {
      setUploading(true);
      if (!chatbotId) return;
      await demoService.uploadFakeBackground(chatbotId, file);
      toast('Fake background uploaded');
    } catch (e) {
      toast('Failed to upload background', { description: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setUploading(false);
      // reset input value to allow re-upload of same file if needed
      e.currentTarget.value = '';
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!settings) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/svg+xml' && file.type !== 'image/png') {
      toast('Only SVG or PNG files are allowed');
      return;
    }
    const maxBytes = 300 * 1024;
    if (file.size > maxBytes) {
      toast('Image must be smaller than 300 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setSettings({ ...settings, logo: dataUrl });
    };
    reader.readAsDataURL(file);
  };

  const handleWelcomeLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!settings) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/svg+xml' && file.type !== 'image/png') {
      toast('Only SVG or PNG files are allowed');
      return;
    }
    const maxBytes = 300 * 1024;
    if (file.size > maxBytes) {
      toast('Image must be smaller than 300 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setSettings({ ...settings, welcomeLogo: dataUrl });
    };
    reader.readAsDataURL(file);
  };

  return (
    <PageLayout
      title="Demo"
      description="Generate demo data for your chatbot"
      showBackToDashboard
    >
      <div className="space-y-6">
        {projects.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Project</CardTitle>
              <CardDescription>
                Select a project to scope demo data. Default is Team.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="min-w-[260px]">
                <Select value={selectedProjectId} onValueChange={(v) => setSelectedProjectId(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TEAM">Team</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader>
            <CardTitle>Chatbot</CardTitle>
            <CardDescription>
              Create a chatbot agent to attach generated demo data.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {loadingSettings ? (
              <div className="text-sm text-muted-foreground">Loading settings…</div>
            ) : !chatbotId ? (
              <Button onClick={handleCreate} disabled={creating} className="btn-primary">
                {creating ? 'Creating…' : 'Create a chatbot'}
              </Button>
            ) : chatbots.length === 0 ? (
              <Button onClick={handleCreate} disabled={creating} className="btn-primary">
                {creating ? 'Creating…' : 'Create new'}
              </Button>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <Label className="text-sm text-muted-foreground">Chatbot</Label>
                <Select value={chatbotId ?? ''} onValueChange={handleSelectChatbot}>
                  <SelectTrigger className="max-w-md">
                    <SelectValue placeholder="Select a chatbot" />
                  </SelectTrigger>
                    <SelectContent>
                      {chatbots.map((cb) => (
                        <SelectItem key={cb.id} value={cb.id}>{cb.id}</SelectItem>
                      ))}
                    </SelectContent>
                </Select>
                {chatbotId && (
                  <Button variant="outline" onClick={() => copyText(chatbotId)}>
                    <Copy className="mr-2 h-4 w-4" /> Copy
                  </Button>
                )}
                {chatbotId && (
                  <div className="flex items-center gap-2 w-full md:w-auto md:min-w-[520px]">
                    <Label className="text-sm text-muted-foreground">Demo URL</Label>
                    <div className="flex items-center gap-2 w-full md:w-auto md:min-w-[520px]">
                      <Input
                        readOnly
                        value={`https://app.recallio.ai/chat/demo.html?id=${chatbotId}`}
                        className="flex-1 min-w-0"
                      />
                      <Button variant="outline" onClick={() => copyText(`https://app.recallio.ai/chat/demo.html?id=${chatbotId}`)}>
                        <Copy className="mr-2 h-4 w-4" /> Copy
                      </Button>
                    </div>
                  </div>
                )}
                <div className="w-full flex items-center gap-3 mt-2">
                  <Button onClick={handleCreate} disabled={creating} className="btn-primary">
                    {creating ? 'Creating…' : 'Create new'}
                  </Button>
                  {chatbotId && (
                    <Button variant="destructive" onClick={handleDelete}>
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {settings && (
          <Card>
            <CardHeader>
              <CardTitle>Customization</CardTitle>
              <CardDescription>Configure initial messages and appearance.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Switch checked={!!settings.styles.auto_open_bubble_messages} onCheckedChange={(v) => updateStyle('auto_open_bubble_messages', v)} />
                      <span className="font-medium">Auto-open welcome & bubble messages</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Label className="whitespace-nowrap">Auto-open after (seconds)</Label>
                      <Input className="w-28" type="number" value={settings.styles.auto_open_bubble_messages_after} onChange={(e) => updateStyle('auto_open_bubble_messages_after', Number(e.target.value))} />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="space-y-3 mb-6">
                    <div className="space-y-2">
                      <Label>Welcome message</Label>
                      <Textarea
                        value={settings.welcomeMessage ?? ''}
                        onChange={(e) => setSettings({ ...settings, welcomeMessage: e.target.value })}
                        placeholder="Welcome message shown above the bubble"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Welcome logo (SVG or PNG, &lt; 300 KB)</Label>
                      <div className="flex items-center gap-4">
                        <Input type="file" accept="image/svg+xml,image/png" onChange={handleWelcomeLogoChange} />
                        {settings.welcomeLogo && (
                          <img src={settings.welcomeLogo} alt="Welcome logo preview" className="h-12 w-auto rounded border" />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="font-medium mb-2">Bubble Messages</div>
                  <div className="space-y-3">
                    {settings.bubbleMessages.map((msg, idx) => (
                      <div key={idx} className="border rounded-lg p-4 space-y-3">
                        <Label>Message</Label>
                        <Input
                          value={msg.message}
                          onChange={(e) => updateBubbleMessage(idx, 'message', e.target.value)}
                          placeholder={`Bubble message #${idx + 1}`}
                        />
                        <Label>Instructions</Label>
                        <Textarea
                          value={msg.instructions}
                          onChange={(e) => updateBubbleMessage(idx, 'instructions', e.target.value)}
                          placeholder="Instructions (optional)"
                        />
                        <div className="flex justify-end">
                          <Button variant="ghost" onClick={() => removeBubbleMessage(idx)} aria-label="Remove">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" onClick={addBubbleMessage} className="gap-2">
                      <Plus className="h-4 w-4" /> Add bubble message
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="font-medium mb-2">Suggested Questions</div>
                  <div className="space-y-3">
                    {settings.initialMessages.map((msg, idx) => (
                      <div key={idx} className="border rounded-lg p-4 space-y-3">
                        <Label>Message</Label>
                        <Input
                          value={msg.message}
                          onChange={(e) => updateInitialMessage(idx, 'message', e.target.value)}
                          placeholder={`Initial message #${idx + 1}`}
                        />
                        <Label>Instructions</Label>
                        <Textarea
                          value={msg.instructions}
                          onChange={(e) => updateInitialMessage(idx, 'instructions', e.target.value)}
                          placeholder="Instructions (optional)"
                        />
                        <div className="flex justify-end">
                          <Button variant="ghost" onClick={() => removeInitialMessage(idx)} aria-label="Remove">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" onClick={addInitialMessage} className="gap-2">
                      <Plus className="h-4 w-4" /> Add message
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="font-medium">Theme</div>
                    <RadioGroup value={settings.styles.theme} onValueChange={(v) => updateStyle('theme', v as 'dark' | 'light')} className="grid grid-cols-2 gap-3">
                      <Label htmlFor="theme-dark" className="border rounded-lg p-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id="theme-dark" value="dark" />
                          <span>Dark</span>
                        </div>
                      </Label>
                      <Label htmlFor="theme-light" className="border rounded-lg p-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id="theme-light" value="light" />
                          <span>Light</span>
                        </div>
                      </Label>
                    </RadioGroup>
                  </div>

                  <div className="space-y-3">
                    <div className="font-medium">Align chat button</div>
                    <RadioGroup value={settings.styles.align_chat_button} onValueChange={(v) => updateStyle('align_chat_button', v as 'left' | 'right')} className="grid grid-cols-2 gap-3">
                      <Label htmlFor="align-left" className="border rounded-lg p-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id="align-left" value="left" />
                          <span>Left</span>
                        </div>
                      </Label>
                      <Label htmlFor="align-right" className="border rounded-lg p-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id="align-right" value="right" />
                          <span>Right</span>
                        </div>
                      </Label>
                    </RadioGroup>
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Logo (SVG or PNG, &lt; 300 KB)</Label>
                  <div className="flex items-center gap-4">
                    <Input type="file" accept="image/svg+xml,image/png" onChange={handleLogoChange} />
                    {settings.logo && (
                      <img src={settings.logo} alt="Logo preview" className="h-12 w-auto rounded border" />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Brand name</Label>
                    <Input value={settings.styles.brand_name} onChange={(e) => updateStyle('brand_name', e.target.value)} placeholder="Brand name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Primary color</Label>
                    <Input type="color" value={settings.styles.primary_color} onChange={(e) => updateStyle('primary_color', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary color</Label>
                    <Input type="color" value={settings.styles.secondary_color} onChange={(e) => updateStyle('secondary_color', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Accent color</Label>
                    <Input type="color" value={settings.styles.accent_color} onChange={(e) => updateStyle('accent_color', e.target.value)} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Message placeholder</Label>
                    <Input value={settings.styles.message_placeholder} onChange={(e) => updateStyle('message_placeholder', e.target.value)} placeholder="Message..." />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Tagline</Label>
                    <Input value={settings.styles.tagline} onChange={(e) => updateStyle('tagline', e.target.value)} placeholder="You can ask me anything" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Welcome message</Label>
                    <Input value={settings.styles.welcome_message} onChange={(e) => updateStyle('welcome_message', e.target.value)} placeholder="Ask anything about transactions, listings..." />
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Describe how the agent should greet the user.</Label>
                  <Textarea
                    value={settings.customGreeting ?? ''}
                    onChange={(e) => setSettings({ ...settings, customGreeting: e.target.value })}
                    placeholder="Describe how the agent should greet the user..."
                    className="h-3"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Customize the way the agent answers questions.</Label>
                  <Textarea
                    value={settings.customInstructions ?? ''}
                    onChange={(e) => setSettings({ ...settings, customInstructions: e.target.value })}
                    placeholder="Describe how the agent should answer questions..."
                    className="h-48"
                  />
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving} className="btn-primary">
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {chatbotId && (
          <Card>
            <CardHeader>
              <CardTitle>Demo data generator</CardTitle>
              <CardDescription>Provide details for the synthetic dataset.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Company name</Label>
                  <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acme Corp" />
                </div>
                <div className="space-y-2">
                  <Label>Description of the demo data</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the type of data to generate..."
                    className="h-48"
                  />
                </div>
                <div>
                  <Button onClick={handleGenerate} disabled={generating} className="btn-primary">
                    {generating ? 'Generating…' : 'Generate data'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {chatbotId && (
          <Card>
            <CardHeader>
              <CardTitle>Fake Data</CardTitle>
              <CardDescription>View or update the JSON/text stored in FakeCustomStorage.</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingFakeData ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
              ) : (
                <div className="space-y-3">
                  <Textarea
                    value={fakeData}
                    onChange={(e) => setFakeData(e.target.value)}
                    className="font-mono text-sm h-64"
                    placeholder="Paste or edit your fake data here..."
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleSaveFakeData} disabled={savingFakeData} className="btn-primary">
                      {savingFakeData ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {chatbotId && (
          <Card>
            <CardHeader>
              <CardTitle>Fake background</CardTitle>
              <CardDescription>Upload a PNG image up to 3 MB that will be used as the demo background.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Background (PNG, ≤ 3 MB)</Label>
                <div className="flex items-center gap-4">
                  <Input type="file" accept="image/png" onChange={handleUploadBackground} disabled={uploading} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
            <CardHeader>
              <CardTitle>Google Calendar</CardTitle>
              <CardDescription>Connect your Google Calendar to your chatbot.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button onClick={handleConnectGoogleCalendar} className="btn-primary">
                  Connect Google Calendar
                </Button>
              </div>
            </CardContent>
          </Card>
      </div>
    </PageLayout>
  );
};

export default Demo;


